import {ObjectAttributeField} from "src/api/generated/erp/common/objectAttributes/objectAttributeField";
import React, {useCallback} from "react";
import {Checkbox, FormControlLabel, TextField} from "@mui/material";
import {MultiSelectField} from "src/components/common/inputFields/MultiSelectField";
import {SelectField} from "src/components/common/inputFields/SelectField";
import {
    ObjectAttributeFieldType,
    ObjectAttributeFieldType_Decimal,
    ObjectAttributeFieldType_Integer,
    ObjectAttributeFieldType_Selection,
    ObjectAttributeFieldType_Text,
} from "src/api/generated/erp/common/objectAttributes/objectAttributeFieldType";
import {SimpleSelectFieldOption} from "src/components/common/inputFields/types";
import {
    checkObjectAttributeFieldDecoding,
    dateObjectAttributeFieldDecoding,
    dateTimeObjectAttributeFieldDecoding,
    decimalObjectAttributeFieldDecoding,
    integerObjectAttributeFieldDecoding,
    multiSelectionObjectAttributeFieldDecoding,
    ObjectAttributeFieldDecoding,
    singleSelectionObjectAttributeFieldDecoding,
    textObjectAttributeFieldDecoding,
} from "src/components/views/erp/common/objectAttributes/attributeValueDecoding";
import {AavoDatePicker} from "src/components/common/inputFields/AavoDatePicker";
import {Dayjs} from "dayjs";
import {AavoDateTimePicker} from "src/components/common/inputFields/AavoDateTimePicker";
import {PathValue, UseControllerProps} from "react-hook-form";
import {FieldPath, useController} from "react-hook-form";
import {requireRule} from "src/components/common/forms/validation";

export interface ObjectAttributeFieldComponentProps<TFieldValues extends object> {
    objectAttributeField: ObjectAttributeField;
    control: UseControllerProps<TFieldValues>["control"];
    name: UseControllerProps<TFieldValues>["name"];
}

export const ObjectAttributeFieldComponent = <TFieldValues extends object>({
                                                                               objectAttributeField,
                                                                               ...other
                                                                           }: ObjectAttributeFieldComponentProps<TFieldValues>) => {
    const fieldType = objectAttributeField.type;
    if (fieldType.type === "checkbox")
        return <BooleanAttributeField objectAttributeField={objectAttributeField} {...other} />;
    else if (fieldType.type === "selection")
        return (
            <SelectionAttributeField
                fieldType={fieldType}
                objectAttributeField={objectAttributeField}
                {...other}
            />
        );
    else if (fieldType.type === "date")
        return <DateAttributeField objectAttributeField={objectAttributeField} {...other} />;
    else if (fieldType.type === "dateTime")
        return <DateTimeAttributeField objectAttributeField={objectAttributeField} {...other} />;
    else
        return (
            <TextualAttributeField
                fieldType={fieldType}
                objectAttributeField={objectAttributeField}
                {...other}
            />
        );
};

const BooleanAttributeField = <TFieldValues extends object>({
                                                                objectAttributeField,
                                                                name,
                                                                control,
                                                            }: ObjectAttributeFieldComponentProps<TFieldValues>) => {
    const {field} = useController({
        name,
        control,
        rules: createRules(objectAttributeField),
    });

    const transformedField = {
        ...field,
        onChange: (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            field.onChange(checkObjectAttributeFieldDecoding.encode(checked));
        },
    };

    return (
        <FormControlLabel
            label={objectAttributeField.label}
            control={
                <Checkbox
                    checked={checkObjectAttributeFieldDecoding.decode(field.value) as boolean}
                    {...transformedField}
                />
            }
        />
    );
};

const DateAttributeField = <TFieldValues extends object>({
                                                             objectAttributeField,
                                                             name,
                                                             control,
                                                         }: ObjectAttributeFieldComponentProps<TFieldValues>) => {
    const {field, fieldState} = useController({
        name,
        control,
        rules: createRules(objectAttributeField),
    });

    const decoding = dateObjectAttributeFieldDecoding;

    const transformedField = {
        ...field,
        value: decoding.decode(field.value),
        onChange: (date: Dayjs | null) => field.onChange(decoding.encode(date)),
        ref: undefined,
    };

    return (
        <AavoDatePicker
            label={objectAttributeField.label}
            errorMessage={fieldState.error?.message}
            {...transformedField}
        />
    );
};

const DateTimeAttributeField = <TFieldValues extends object>({
                                                                 objectAttributeField,
                                                                 name,
                                                                 control,
                                                             }: ObjectAttributeFieldComponentProps<TFieldValues>) => {
    const {field, fieldState} = useController({
        name,
        control,
        rules: createRules(objectAttributeField),
    });
    const decoding = dateTimeObjectAttributeFieldDecoding;

    const transformedField = {
        ...field,
        value: decoding.decode(field.value),
        onChange: (date: Dayjs | null) => field.onChange(decoding.encode(date)),
        ref: undefined,
    };

    return (
        <AavoDateTimePicker
            label={objectAttributeField.label}
            errorMessage={fieldState.error?.message}
            {...transformedField}
        />
    );
};

interface SelectionAttributeFieldProps<TFieldValues extends object>
    extends ObjectAttributeFieldComponentProps<TFieldValues> {
    fieldType: ObjectAttributeFieldType_Selection;
}

const SelectionAttributeField = <TFieldValues extends object>({
                                                                  objectAttributeField,
                                                                  name,
                                                                  control,
                                                                  fieldType,
                                                              }: SelectionAttributeFieldProps<TFieldValues>) => {
    const {field, fieldState} = useController({
        name,
        control,
        rules: createRules(objectAttributeField),
    });

    const optionsList: SimpleSelectFieldOption<string>[] = Object.entries(fieldType.options).map(
        ([key, label]) => {
            return {
                key: key,
                label: label,
            };
        },
    );

    if (fieldType.multiSelect) {
        const decoding = multiSelectionObjectAttributeFieldDecoding;

        const transformedField = {
            ...field,
            value: decoding.decode(field.value),
            onChange: (optionKeys: string[]) => {
                field.onChange(decoding.encode(optionKeys));
            },
            ref: undefined,
        };

        return (
            <MultiSelectField
                getOptionKey={(o) => o.key}
                getOptionLabel={(o) => o.label}
                label={objectAttributeField.label}
                options={optionsList}
                error={fieldState.error?.message}
                {...transformedField}
            />
        );
    } else {
        const decoding = singleSelectionObjectAttributeFieldDecoding;

        const transformedField = {
            ...field,
            value: decoding.decode(field.value),
            onChange: (key: string | null | undefined) => {
                field.onChange(decoding.encode(key ?? undefined));
            },
            ref: undefined,
        };

        return (
            <SelectField
                getOptionKey={(o) => o.key}
                getOptionLabel={(o) => o.label}
                label={objectAttributeField.label}
                options={optionsList}
                error={fieldState.error?.message}
                {...transformedField}
            />
        );
    }
};

interface TextualAttributeFieldProps<TFieldValues extends object>
    extends ObjectAttributeFieldComponentProps<TFieldValues> {
    fieldType: Extract<
        ObjectAttributeFieldType,
        | ObjectAttributeFieldType_Text
        | ObjectAttributeFieldType_Integer
        | ObjectAttributeFieldType_Decimal
    >;
}

const TextualAttributeField = <TFieldValues extends object>({
                                                                objectAttributeField,
                                                                name,
                                                                control,
                                                                fieldType,
                                                            }: TextualAttributeFieldProps<TFieldValues>) => {
    let decoding: ObjectAttributeFieldDecoding<any>;
    let textFieldType: string;
    let defaultValue: string | number;
    switch (fieldType.type) {
        case "text":
            decoding = textObjectAttributeFieldDecoding;
            textFieldType = "text";
            defaultValue = "";
            break;
        case "integer":
            decoding = integerObjectAttributeFieldDecoding;
            textFieldType = "number";
            defaultValue = 0;
            break;
        case "decimal":
            decoding = decimalObjectAttributeFieldDecoding;
            textFieldType = "number";
            defaultValue = 0;
            break;
    }

    const {field, fieldState} = useController({
        name,
        control,
        rules: createRules(objectAttributeField),
        defaultValue: defaultValue as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    });

    const onChangeWrapped = useCallback(
        (e: {
            target: {
                value: string;
            };
        }) => {
            const value = e.target.value;
            if (decoding.inputAllowed?.(value) ?? true) field.onChange(decoding.encode(value));
        },
        [field, decoding],
    );

    return (
        <TextField
            type={textFieldType}
            value={decoding.decode(field.value)}
            label={objectAttributeField.label}
            onChange={onChangeWrapped}
            error={fieldState.error !== undefined}
            helperText={fieldState.error?.message}
        ></TextField>
    );
};

const createRules = (field: ObjectAttributeField) => {
    if (field.required) return requireRule();
    return {};
};
